import type { Network3DViewSettings } from '@ardoq/xyz-charts';
import type { I18nLocale } from '@ardoq/i18n';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import type { Network3DNodeSizeBy } from './sourceData/toNodeSizing';

/**
 * The app's settings for this view.
 *
 * The package's rendering dials, plus three the package has no business knowing about: the flag for
 * Ardoq's own view legend, and the pair that says what mark size stands for. Size is resolved here
 * rather than there because the quantities on offer are Ardoq's - a reference count and a numeric
 * field on a component - and the package takes only the resolved number, as `NetworkNode.weight`.
 */
export type Network3DAppViewSettings = Network3DViewSettings & {
  isLegendActive: boolean;
  nodeSizeBy: Network3DNodeSizeBy;
  /** Only read when `nodeSizeBy` is `'field'`. Empty is how a dropdown persists "off". */
  sizeByFieldName: string;
  /**
   * The groups drawn closed, by mark id, with `WILDCARD` standing for every one.
   *
   * The same setting and the same convention every other grouping view stores - see
   * `isGroupCollapsed` in `buildBlocksGraph`. The setting *is* the state: the graph is rebuilt
   * whenever this changes, so a stored one is honoured on load without anything having to
   * re-apply it, and there is no fold state living inside a canvas to die with it.
   */
  collapsedGroupIds: string[];
};

/**
 * What the view publishes for the settings bar to read.
 *
 * A narrow slice of the view's props, as the XYZ charts' equivalents are: the settings bar needs
 * the settings to tick the active options, the numeric fields to fill the size-by picker, and
 * `isDrawable` to know whether Export to PNG has anything to offer.
 */
export type Network3DViewProperties = {
  viewSettings: Network3DAppViewSettings;
  /**
   * The size picker's options, from the scope the sizing was resolved against.
   *
   * On the properties rather than derived in the settings bar, for the reason the XYZ charts give:
   * the bar has to offer the list the derivation used, or a reader picks a field the picture
   * ignores.
   */
  numericFields: FieldWithPermissions[];
  i18nLocale: I18nLocale;
  isEmptyView: boolean;
  /**
   * Whether there is a canvas to export, which is a different question from whether the viewpoint
   * has components.
   *
   * False for the session on a browser with no WebGPU adapter: the view draws a message, so the
   * source data looks perfectly drawable and the export finds nothing. The surface and contour
   * charts carry the same flag for the same reason - a failure toast from an enabled menu entry.
   */
  isDrawable: boolean;
  /**
   * Whether the grouping has a group in it, which is what Collapse all and Expand all gate on.
   *
   * Not `groupingRules.length > 0`. A rule that resolves to one group per component produces a
   * forest of childless roots, and a Collapse all that does nothing reads as a broken view rather
   * than as a viewpoint with nothing to collapse.
   */
  hasGroups: boolean;
  /**
   * Whether a grouping rule is arranging the view, which overrides the Layout setting.
   *
   * Not the same question as `hasGroups`, which is whether there is a group to collapse. This one
   * is whether the reader's layout choice is in effect at all - `drawnSettings` rewrites
   * `layoutKind` to `grouped` whenever a rule is on, because only that layout can express a
   * grouping, and the control has to say so rather than naming something else.
   */
  isGrouped: boolean;
  viewInstanceId: string;
};

const NETWORK_3D_VIEW_PROPERTIES_KEYS = [
  'viewSettings',
  'numericFields',
  'isDrawable',
  'hasGroups',
  'isGrouped',
  'i18nLocale',
  'isEmptyView',
  'viewInstanceId',
] as const satisfies readonly (keyof Network3DViewProperties)[];

/**
 * The view-pane header reads these off an untyped record, so a mismatch would throw inside a
 * reducer rather than degrade to undefined.
 */
export const isNetwork3DViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is Network3DViewProperties =>
  NETWORK_3D_VIEW_PROPERTIES_KEYS.every(
    key => viewProperties[key] !== undefined
  );
